import { applySnapshot, types } from 'mobx-state-tree';
import moment from 'moment';

import { rootStore } from '.';

import WorkTypeService from 'services/WorkTypeService';
import GroupService from 'services/GroupService';
import WorkSchduleService from 'services/WorkSchduleService';

export const WorkingFlex = types
    .model('WorkingFlex', {
        workTypeList: types.optional(types.array(types.frozen()), []),
        groupWorkTypes: types.optional(types.array(types.frozen()), []),
        isLoading: types.optional(types.boolean, false),
        workSummaryData: types.optional(types.frozen(), {}),
        filterWorkSummaryData: types.optional(types.frozen(), {}),
        groupUsers: types.optional(types.frozen(), []),
        groupUsersFiltered: types.optional(types.frozen(), []),
        groupUsersSource: types.optional(types.frozen(), {}),
        scheduleItems: types.optional(types.frozen(), []),
        weeklyScheduleItems: types.optional(types.frozen(), []),
        filterFlexibleScheduleData: types.optional(types.frozen(), {}),
        filterParams: types.optional(types.frozen(), {}),
        selectedWorkType: types.optional(types.frozen(), null),
    })
    .volatile(() => ({
        setOriginalDataFn: null,
    }))
    .views((self) => ({
        get get() {
            try {
                return { ...self };
            } catch (err) {
                return err;
            }
        },
        get workFlexSummary() {
            return self.workSummaryData;
        },
    }))
    .actions((self) => ({
        reset() {
            applySnapshot(self, {});
        },
        setWorkTypeList(list = []) {
            self.workTypeList.replace(list.filter((i) => i?.abntdAgwrkExpsrYn === true));
        },
        setFilterParams(params = {}) {
            self.filterParams = {
                ...self.filterParams,
                ...params,
            };
        },
        setSelectedWorkType(value = null) {
            self.selectedWorkType = value;
        },
        setGroupWorkTypes(list = []) {
            const res = list
                .filter((i) => i?.abntdAgwrkExpsrYn === true)
                .map((item) => ({
                    id: item.abntdWorkTypCd,
                    title: item.abntdWorkTypNm,
                    name: item.abntdWorkTypNm,
                    // 표시용 번역명(getLocalizedField). name은 원본 그대로 둔다
                    nameEng: item.nameEng,
                    nameVie: item.nameVie,
                    nameChn: item.nameChn,
                    color: item.color,
                    root: true,
                    parent: null,
                    users: [],
                }));

            self.groupWorkTypes.replace(res);
        },
        setWorkSummaryData(data = {}) {
            self.workSummaryData = data;
        },
        setIsLoading(value) {
            self.isLoading = value;
        },
        setFilterWorkSummaryData(data = {}) {
            self.filterWorkSummaryData = data;
        },
        setGroupUsers(data = {}) {
            self.groupUsersSource = data || {};
            const groupWorkTypes = self.groupWorkTypes.toJSON();
            const selectedWorkType = self.selectedWorkType;
            const scheduleGroupSet = new Set(
                (self.scheduleItems || []).map((item) => item?.group).filter(Boolean),
            );

            const groupWithUsers = groupWorkTypes
                .filter((group) => {
                    if (!selectedWorkType || selectedWorkType === 'ALL') {
                        return true;
                    }
                    return group.id?.toString() === selectedWorkType?.toString();
                })
                .map((group) => {
                    const users = data[group.id?.toString()] || [];
                    const matchedUsers = users.filter((user) => {
                        const userGroupId = `${group.id?.toString()}-${user?.userId?.toString()}`;
                        return scheduleGroupSet.has(userGroupId);
                    });

                    return {
                        ...group,
                        users: matchedUsers,
                    };
                });

            self.groupUsers = groupWithUsers;

            self.setOriginalDataToHook(groupWithUsers);
        },
        filterGroupByWorkType() {
            const sourceData = self.groupUsersSource || {};
            const groupWorkTypes = self.groupWorkTypes.toJSON();
            const selectedWorkType = self.selectedWorkType;
            const scheduleGroupSet = new Set(
                (self.scheduleItems || []).map((item) => item?.group).filter(Boolean),
            );

            const groupWithUsers = groupWorkTypes
                .filter((group) => {
                    if (!selectedWorkType || selectedWorkType === 'ALL') {
                        return true;
                    }
                    return group.id?.toString() === selectedWorkType?.toString();
                })
                .map((group) => {
                    const users = sourceData[group.id?.toString()] || [];
                    const matchedUsers = users.filter((user) => {
                        const userGroupId = `${group.id?.toString()}-${user?.userId?.toString()}`;
                        return scheduleGroupSet.has(userGroupId);
                    });

                    return {
                        ...group,
                        users: matchedUsers,
                    };
                });

            self.groupUsers = groupWithUsers;
            self.setOriginalDataToHook(groupWithUsers);
        },
        setFilterFnToModel(cb) {
            self.setOriginalDataFn = typeof cb === 'function' ? cb : null;
        },
        setOriginalDataToHook(data) {
            if (typeof self.setOriginalDataFn === 'function') {
                self.setOriginalDataFn(data);
            }
        },
        setGroupUsersFiltered(data = []) {
            self.groupUsersFiltered = data;
        },
        setScheduleItems(list = []) {
            const items = list
                ?.map((item, index) => {
                    const actualStart = moment(item?.start_time);
                    const actualEnd = moment(item?.end_time);

                    if (!actualStart.isValid() || !actualEnd.isValid()) {
                        return null;
                    }

                    const dayStart = actualStart.clone().startOf('day');
                    const dayEnd = dayStart.clone().add(1, 'day');
                    const groupId = `${item?.row?.abntdWorkTypCd}-${item?.userId}`;

                    return {
                        ...item,
                        id:
                            item?.id?.toString() ||
                            `timeline-item-${groupId}-${dayStart.valueOf()}-${index}`,
                        group: groupId,
                        start_time: dayStart,
                        end_time: dayEnd,
                        actual_start_time: actualStart,
                        actual_end_time: actualEnd,
                    };
                })
                .filter(Boolean);

            self.scheduleItems = items;
        },
        setWeeklyScheduleItems(list = []) {
            const items = list
                ?.map((item, index) => {
                    const actualStart = moment(item?.start_time);
                    const actualEnd = moment(item?.end_time);

                    if (!actualStart.isValid() || !actualEnd.isValid()) {
                        return null;
                    }

                    const dayStart = actualStart.clone().startOf('day');
                    const dayEnd = dayStart.clone().add(1, 'day');
                    const groupId = `${item?.row?.abntdWorkTypCd}-${item?.userId}`;

                    return {
                        ...item,
                        id:
                            item?.id?.toString() ||
                            `timeline-item-${groupId}-${dayStart.valueOf()}-${index}`,
                        group: groupId,
                        start_time: dayStart,
                        end_time: dayEnd,
                        actual_start_time: actualStart,
                        actual_end_time: actualEnd,
                    };
                })
                .filter(Boolean);
            self.weeklyScheduleItems = items;
        },
        setFilterScheduleData(data = {}) {
            self.filterFlexibleScheduleData = data;
        },
        async getWorkTypeList(t) {
            try {
                self.setIsLoading(true);

                const companyId = rootStore?.company?.get?.id;
                if (!companyId) {
                    self.setWorkTypeList([]);
                    return [];
                }

                const response = await WorkTypeService.list(companyId);
                const list = response?.data || [];
                self.setWorkTypeList(list);
                self.setGroupWorkTypes(list);
                await self.getGroupUsers();
                await self.getScheduleItems();
                return list;
            } catch (error) {
                self.setWorkTypeList([]);
                self.setGroupWorkTypes([]);
                console.error('Error fetching work type list:', error);
                return [];
            } finally {
                self.setIsLoading(false);
            }
        },
        async getFlexibleWorkSummaryData(filter = {}) {
            try {
                self.setIsLoading(true);

                const companyId = rootStore?.company?.get?.id;
                if (!companyId) {
                    self.setWorkSummaryData([]);
                    return [];
                }

                const response = await WorkTypeService.getFlexibleWorkSummaryData({
                    companyId,
                    ...filter,
                });

                const list = response?.data || [];
                self.setFilterWorkSummaryData(filter);
                self.setWorkSummaryData(list);
                return list;
            } catch (error) {
                self.setWorkSummaryData([]);
                console.error('Error fetching work summary data:', error);
                return [];
            } finally {
                self.setIsLoading(false);
            }
        },
        async getGroupUsers() {
            try {
                self.setIsLoading(true);

                const companyId = rootStore?.company?.get?.id;
                const { startDate, endDate } = self.filterParams || {};

                const response = await GroupService.searchFlexibleSchedule({
                    companyId,
                    isUser: true,
                    startDate,
                    endDate,
                    workSystemName: '유연',
                });

                const data = response?.data?.virtualGroups || {};

                self.setGroupUsers(data);
                return data;
            } catch (error) {
                self.setGroupUsers([]);
                console.error('Error fetching flexible schedule data:', error);
                return [];
            } finally {
                self.setIsLoading(false);
            }
        },
        async getScheduleItems() {
            try {
                self.setIsLoading(true);

                const companyId = rootStore?.company?.get?.id;
                const { startDate, endDate } = self.filterParams || {};

                const response = await WorkSchduleService.getScheduleList4Flex({
                    companyId,
                    sdate: startDate,
                    edate: endDate,
                    workSystemName: '유연',
                });

                const list = response?.data || [];

                self.setScheduleItems(list);
                if (Object.keys(self.groupUsersSource || {}).length > 0) {
                    self.filterGroupByWorkType();
                }
                return list;
            } catch (error) {
                self.setScheduleItems([]);
                console.error('Error fetching flexible schedule list:', error);
                return [];
            } finally {
                self.setIsLoading(false);
            }
        },
        async getWeeklyScheduleItems() {
            try {
                self.setIsLoading(true);

                const { startDate, endDate } = self.filterParams || {};
                const companyId = rootStore?.company?.get?.id;
                const weekStartDate = moment(startDate).startOf('isoWeek').format('YYYY-MM-DD');
                const weekEndDate = moment(endDate).endOf('isoWeek').format('YYYY-MM-DD');

                const response = await WorkSchduleService.getScheduleList4Flex({
                    companyId,
                    sdate: weekStartDate,
                    edate: weekEndDate,
                    workSystemName: '유연',
                });

                const list = response?.data || [];

                self.setWeeklyScheduleItems(list);
                return list;
            } catch (error) {
                self.setWeeklyScheduleItems([]);
                console.error('Error fetching flexible weekly schedule list:', error);
                return [];
            } finally {
                self.setIsLoading(false);
            }
        },
        async initialize(t) {
            await self.getWorkTypeList(t);
        },
    }));

export default WorkingFlex;
